import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addToast } from '@/store/app/action';
import { PrimaryButton, SecondaryButton } from '@/components/Buttons';
import {
  createPOSCredentials,
  updatePOSCredentials,
  testPOSCredentials,
} from '@/api/posCredentials';
import {
  fieldsFromDescriptor,
  initialValuesFor,
  missingRequired,
  providerPresentation,
} from '../providerCatalog';

/**
 * The signed CSV link a successful test returns.
 *
 * A passing test pulls the store's menu from the provider and parks it in S3,
 * handing back a short-lived pre-signed URL. It is read straight off the
 * response and never stored or rebuilt — once it expires the only way to get
 * another is to run the test again.
 *
 * The scheme check is because this goes into an `href`: the value comes from a
 * response body, so it should not be able to turn into a `javascript:` URL.
 */
const menuUrlFrom = (result) => {
  const url = result && typeof result === 'object' ? result.url : null;
  return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
};

// Written out in full rather than composed, because Tailwind only ships the
// class names it can see in the source.
const RESULT_TONES = {
  success: {
    panel: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20',
    title: 'text-emerald-800 dark:text-emerald-200',
    body: 'text-emerald-700 dark:text-emerald-300',
  },
  warn: {
    panel: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20',
    title: 'text-amber-800 dark:text-amber-200',
    body: 'text-amber-700 dark:text-amber-300',
  },
  error: {
    panel: 'border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20',
    title: 'text-rose-800 dark:text-rose-200',
    body: 'text-rose-700 dark:text-rose-300',
  },
};

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.message) return data.message;
  if (data && typeof data === 'object') return JSON.stringify(data, null, 2);
  return error?.message || fallback;
};

/**
 * The credential form for a non-OAuth provider.
 *
 * Every input here is derived from the provider descriptor the catalogue
 * returned rather than written out per provider, which is what lets a provider
 * added server-side work without a dashboard change.
 */
const CredentialsForm = ({ descriptor, storeId, saved, onClose, onConnectionSuccess }) => {
  const dispatch = useDispatch();
  const presentation = providerPresentation(descriptor?.provider);

  const fields = useMemo(() => fieldsFromDescriptor(descriptor), [descriptor]);
  const [values, setValues] = useState(() => initialValuesFor(descriptor, saved, { storeId }));
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  const isEditing = !!saved && Object.keys(saved).length > 0;
  const missing = missingRequired(descriptor, values);

  // A pass with nothing to download is its own outcome, not a plain success:
  // the provider took the details but handed back no menu.
  const resultTone = testResult
    ? RESULT_TONES[testResult.ok ? (testResult.menuUrl ? 'success' : 'warn') : 'error']
    : null;

  const setValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // Any edit invalidates a previous test result — showing a stale green tick
    // next to changed credentials would be worse than showing nothing.
    setTestResult(null);
  };

  const guardRequired = () => {
    if (missing.length === 0) return true;
    setError(`Please fill in all required fields: ${missing.join(', ')}`);
    return false;
  };

  const handleTest = async () => {
    if (!guardRequired()) return;
    setBusy(true);
    setError(null);
    setTestResult(null);
    try {
      const result = await testPOSCredentials(storeId, descriptor?.provider, values);
      setTestResult({ ok: true, menuUrl: menuUrlFrom(result) });
    } catch (err) {
      setTestResult({ ok: false, detail: errorMessage(err, 'The provider rejected these details.') });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!guardRequired()) return;
    setBusy(true);
    setError(null);
    try {
      if (isEditing) {
        await updatePOSCredentials(storeId, descriptor.provider, values);
      } else {
        await createPOSCredentials(storeId, descriptor.provider, values);
      }
      dispatch(
        addToast({
          type: 'success',
          message: `${presentation.name} ${isEditing ? 'updated' : 'connected'}`,
          timer: 5,
        })
      );
      if (onConnectionSuccess) await onConnectionSuccess();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to save the connection.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {descriptor?.instructions && (
        <div className="rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 p-4">
          <p className="text-sm text-sky-800 dark:text-sky-200 whitespace-pre-line">
            {descriptor.instructions}
          </p>
        </div>
      )}

      {fields.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          This provider declares no configurable fields.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((field) => {
            if (field.type === 'boolean') {
              return (
                <label
                  key={field.key}
                  className="flex items-start gap-2 cursor-pointer sm:col-span-2"
                >
                  <input
                    type="checkbox"
                    checked={!!values[field.key]}
                    onChange={(e) => setValue(field.key, e.target.checked)}
                    className="mt-0.5 form-checkbox"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{field.label}</span>
                </label>
              );
            }

            return (
              <div key={field.key}>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {field.label}
                  {field.required && <span className="text-rose-500 ml-1">*</span>}
                </label>
                <input
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={values[field.key] ?? ''}
                  onChange={(e) =>
                    setValue(
                      field.key,
                      field.type === 'number'
                        ? e.target.value === ''
                          ? ''
                          : parseFloat(e.target.value)
                        : e.target.value
                    )
                  }
                  spellCheck={false}
                  autoComplete="off"
                  className="w-full px-3 py-2 font-mono text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
              </div>
            );
          })}
        </div>
      )}

      {testResult && (
        <div className={`rounded-lg border p-3 ${resultTone.panel}`}>
          <p className={`text-sm font-medium ${resultTone.title}`}>
            {!testResult.ok
              ? 'Test failed'
              : testResult.menuUrl
                ? 'These details work.'
                : 'Connected, but no menu came back.'}
          </p>

          {testResult.ok && testResult.menuUrl && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {/*
                `download` is advisory here: the browser ignores it on a
                cross-origin URL, so the filename is whatever the signed link's
                own content-disposition says. Opening in a new tab keeps the
                half-filled form on screen either way.
              */}
              <a
                href={testResult.menuUrl}
                download={`${storeId}-menu.csv`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Download menu (CSV)
              </a>
              <span className={`text-xs ${resultTone.body}`}>
                Signed link — run the test again once it expires.
              </span>
            </div>
          )}

          {testResult.ok && !testResult.menuUrl && (
            <p className={`mt-1 text-sm ${resultTone.body}`}>
              The provider accepted these details but returned no menu file, so there is nothing
              to download.
            </p>
          )}

          {!testResult.ok && (
            <p className={`mt-1 text-sm font-mono break-words ${resultTone.body}`}>
              {testResult.detail}
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
          <p className="text-sm text-rose-700 dark:text-rose-300 whitespace-pre-wrap break-words">
            {error}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
        <SecondaryButton onClick={handleTest} disabled={busy}>
          Test connection
        </SecondaryButton>
        <div className="flex gap-3">
          <SecondaryButton onClick={onClose} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={busy} loading={busy}>
            {isEditing ? 'Save changes' : 'Connect'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default CredentialsForm;
